import React, { useCallback } from "react";
import { useQuery, useMutation } from "@apollo/client";
import useToggle from "react-use/lib/useToggle";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import {
  useRouteMatch,
  Link,
  useParams,
  NavLink,
  useHistory,
} from "react-router-dom";

import { QUERY_CLIENT_SENDGRID_LIST } from "../../../config/graphql/query";
import { DELETE_CLIENT_SENDGRID } from "../../../config/graphql/mutation";
import Pagination from "../../../components/Pagination";
import { toast } from "react-toastify";

const limit = 10;

const TableRow = ({
  item,
  index,
}: {
  item: IClientSendGrid;
  index: number;
}) => {
  const { url } = useRouteMatch();
  const { clientId } = useParams<{ clientId: string }>();
  const history = useHistory();

  const { t } = useTranslation(["client", "common"]);

  const { id, title, apiKey, senderEmail, senderName } = item;

  const [visible, setVisible] = useToggle(false);

  const [onDelete] = useMutation(DELETE_CLIENT_SENDGRID, {
    refetchQueries: [
      {
        query: QUERY_CLIENT_SENDGRID_LIST,
        variables: {
          filter: { client: clientId },
        },
      },
    ],
    variables: { input: { id, client: clientId } },
  });

  const onBeforeDelete = () => {
    onDelete()
      .then(() => {
        toast.success(t("client:client.sendgrid.toast.deleted"));
        history.replace(`/client/${clientId}/sendgrid`);
      })
      .catch((error) => {
        toast.error(
          error?.networkError?.result?.errors?.[0]?.message ?? error?.message
        );

        history.replace(`/client/${clientId}/sendgrid`);
      });

    setVisible(false);
  };

  return (
    <>
      <tr>
        <th scope="row">{index + 1}</th>
        <td>{title}</td>
        <td>{`${apiKey.slice(0, 10)}...`}</td>
        <td>
          {senderEmail}
          {senderName ? ` - ${senderName}` : ""}
        </td>
        <td>
          <div className="d-flex">
            <Button
              size="sm"
              variant="danger"
              className="mr-2"
              onClick={() => setVisible(true)}
            >
              {t("common:delete")}
            </Button>
            <Link to={`${url}/${id}`}>
              <Button size="sm" variant="primary">
                {t("common:view")}
              </Button>
            </Link>
          </div>
        </td>
      </tr>
      <Modal
        size="sm"
        show={visible}
        onHide={setVisible}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("client:client.sendgrid.modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.sendgrid.modal.body", { id: id })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const ClientSendGridList = React.memo(() => {
  const match = useRouteMatch();
  const { clientId } = useParams<{ clientId: string }>();

  const { t } = useTranslation(["client", "common"]);

  const { data } = useQuery(QUERY_CLIENT_SENDGRID_LIST, {
    fetchPolicy: "network-only",
    skip: !clientId,
    variables: {
      filter: { client: clientId },
    },
  });

  const clientSendGridArr = data?.sendGridClients ?? [];
  const clientTokensCount = clientSendGridArr.length ?? 0;

  const renderClientSendGrid = useCallback(
    (item: IClientSendGrid, index) => (
      <TableRow key={item.id} item={item} index={index} />
    ),
    []
  );

  return (
    <div className="container-fluid p-0">
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <NavLink exact className="nav-link" to={match.url}>
            {t("client:client.sendgrid.nav.apikeys")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className="nav-link"
            to={`${match.url.replace("sendgrid", "sendgrid-templates")}`}
          >
            {t("client:client.sendgrid.nav.templates")}
          </NavLink>
        </li>
      </ul>

      <div className="tab-content">
        <div className="d-flex justify-content-end align-items-center my-4">
          <div>
            <Link
              to={`${match.url}/new`}
              type="button"
              className="btn btn-primary"
            >
              {t("client:client.tokens.button.add")}
            </Link>
          </div>
        </div>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">{t("client:client.tokens.th.title")}</th>
              <th scope="col">{t("client:client.sendgrid.th.apikey")}</th>
              <th scope="col">{t("client:client.sendgrid.th.senderName")}</th>
              <th scope="col">{t("client:client.tokens.th.actions")}</th>
            </tr>
          </thead>
          <tbody>{clientSendGridArr.map(renderClientSendGrid)}</tbody>
        </Table>
        <Pagination documentsCount={clientTokensCount} limit={limit} />
      </div>
    </div>
  );
});

export default ClientSendGridList;
